import React from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
const HERO_BG_DESKTOP = 'https://cdn.magicpatterns.com/uploads/m4koWPTgG8DAeDjwDgnQuT/ChatGPT_Image_May_26,_2026,_02_24_35_AM.png';
const HERO_BG_MOBILE = 'https://cdn.magicpatterns.com/uploads/xtuGTaqWgoE3wibMfeR8rR/ChatGPT_Image_May_26,_2026,_02_26_12_AM.png';
const ICON_LOWER_PRICE = 'https://cdn.magicpatterns.com/uploads/iDdnjmNHgWMgzXpcUDbmXJ/Mask_group_(13).png';
const ICON_RECOMMENDATIONS = 'https://cdn.magicpatterns.com/uploads/iKCwBaqB4DaB2fFisd2rf5/Mask_group_(14).png';
const ICON_COMMISSION = 'https://cdn.magicpatterns.com/uploads/pg8dMYDz9nDhdnCEEKFYNm/Mask_group_(15).png';
const AVATARS = ['https://i.pravatar.cc/100?img=1', 'https://i.pravatar.cc/100?img=5', 'https://i.pravatar.cc/100?img=8', 'https://i.pravatar.cc/100?img=12'];
const REGISTER_URL = 'https://tokoteta.id/auth/register';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  const featureIcons = [ICON_LOWER_PRICE, ICON_RECOMMENDATIONS, ICON_COMMISSION];
  return <>
      {/* ============ DESKTOP HERO ============ */}
      <section className="hidden md:flex relative w-full h-screen min-h-[640px] max-h-[900px] overflow-hidden bg-black flex-col" data-id="element-174">
        {/* Background image */}
        <div className="absolute inset-0" data-id="element-175">
          <img src={HERO_BG_DESKTOP} alt="" aria-hidden="true" className="w-full h-full object-cover object-center" data-id="element-176" />
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 35%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0) 75%)'
        }} data-id="element-177" />
        </div>

        {/* Content area */}
        <div className="relative flex-1 flex items-center px-6 lg:px-10 pt-20" data-id="element-178">
          <div className="max-w-7xl w-full mx-auto" data-id="element-179">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="max-w-xl text-white" data-id="element-180">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] mb-4 tracking-tight" data-id="element-181">
                {t.hero.title}
              </h1>

              <p className="text-sm lg:text-base text-white/80 mb-6 max-w-md leading-relaxed" data-id="element-182">
                {t.hero.description}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6" data-id="element-183">
                {t.hero.features.map((feature, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.4,
                delay: 0.2 + index * 0.1
              }} className="flex items-center gap-3" data-id="element-184">
                    <img src={featureIcons[index]} alt="" aria-hidden="true" className="w-10 h-10 lg:w-11 lg:h-11 object-contain shrink-0" data-id="element-185" />
                    <div data-id="element-186">
                      <h3 className="text-sm lg:text-base font-bold text-white leading-tight" data-id="element-187">
                        {feature.title}
                      </h3>
                      <p className="text-xs lg:text-sm text-white/65 leading-snug" data-id="element-188">
                        {feature.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>

              {/* CTA */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.6
            }} data-id="element-189">
                <a href={REGISTER_URL} className="inline-flex items-center justify-center gap-2 bg-[#0288F2] hover:bg-[#0674CA] text-white font-semibold px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-xl text-sm lg:text-base" data-id="element-190">
                  {t.hero.ctaPrimary}
                  <ArrowRight size={18} data-id="element-191" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Trust strip - desktop */}
        <div className="relative z-10 pb-5 px-6 lg:px-10" data-id="element-192">
          <div className="max-w-7xl mx-auto" data-id="element-193">
            <div className="bg-white/95 backdrop-blur-sm rounded-full px-5 py-2.5 flex items-center gap-4 lg:gap-6 shadow-lg flex-wrap" data-id="element-194">
              <div className="flex items-center gap-3" data-id="element-195">
                <div className="flex -space-x-2" data-id="element-196">
                  {AVATARS.slice(0, 3).map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-7 h-7 rounded-full border-2 border-white object-cover" data-id="element-197" />)}
                </div>
                <span className="text-sm font-medium text-gray-700" data-id="element-198">
                  {t.hero.trust.text}
                </span>
              </div>
              <div className="h-5 w-px bg-gray-300 hidden sm:block" data-id="element-199" />
              <div className="flex items-center gap-2" data-id="element-200">
                <ShieldCheck size={18} className="text-[#A855F7]" data-id="element-201" />
                <span className="text-sm font-medium text-gray-700" data-id="element-202">
                  {t.hero.trust.tags}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MOBILE HERO ============ */}
      <section className="md:hidden relative w-full h-screen min-h-[600px] max-h-[900px] overflow-hidden bg-black flex flex-col" data-id="element-203">
        {/* Background image - full screen on mobile */}
        <div className="absolute inset-0" data-id="element-204">
          <img src={HERO_BG_MOBILE} alt="" aria-hidden="true" className="w-full h-full object-cover object-center" data-id="element-205" />
          {/* Top-left subtle dark fade for title */}
          <div className="absolute top-0 left-0 right-0 h-[40%] pointer-events-none" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0) 65%)'
        }} data-id="element-206" />
          {/* Bottom white fade for content readability */}
          <div className="absolute bottom-0 left-0 right-0 h-[55%] pointer-events-none" style={{
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 35%, rgba(255,255,255,1) 60%)'
        }} data-id="element-207" />
        </div>

        {/* Title area - top */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative pt-20 px-5 text-white z-10" data-id="element-208">
          <h1 className="text-3xl font-bold leading-[1.05] mb-2 tracking-tight max-w-[130px]" data-id="element-209">
            {t.hero.title}
          </h1>
          <p className="text-xs text-white/90 leading-relaxed max-w-[170px]" data-id="element-210">
            {t.hero.description}
          </p>
        </motion.div>

        {/* Spacer to push content down */}
        <div className="flex-1" data-id="element-211" />

        {/* Bottom content */}
        <div className="relative z-10 px-4 pb-4" data-id="element-212">
          {/* Feature card */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 divide-y divide-gray-100 mb-3 overflow-hidden" data-id="element-213">
            {t.hero.features.map((feature, index) => <div key={index} className="flex items-center gap-3 p-2.5" data-id="element-214">
                <img src={featureIcons[index]} alt="" aria-hidden="true" className="w-9 h-9 object-contain shrink-0" data-id="element-215" />
                <div className="flex-grow min-w-0" data-id="element-216">
                  <h3 className="text-xs font-bold text-gray-900 leading-tight" data-id="element-217">
                    {feature.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 leading-snug" data-id="element-218">
                    {feature.desc}
                  </p>
                </div>
                <ArrowRight size={14} className="text-gray-400 shrink-0" data-id="element-219" />
              </div>)}
          </div>

          {/* CTA */}
          <a href={REGISTER_URL} className="flex items-center justify-center gap-2 bg-[#0288F2] hover:bg-[#0674CA] text-white font-semibold py-3 rounded-full transition-all shadow-md text-sm mb-3" data-id="element-220">
            {t.hero.ctaPrimary}
            <ArrowRight size={16} data-id="element-221" />
          </a>

          {/* Trust strip - responsive */}
          <div className="bg-gray-50 rounded-xl px-3 py-2.5" data-id="element-222">
            <div className="flex items-center gap-2 mb-1.5" data-id="element-223">
              <div className="flex -space-x-1.5 shrink-0" data-id="element-224">
                {AVATARS.map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-5 h-5 rounded-full border-[1.5px] border-white object-cover" data-id="element-225" />)}
              </div>
              <span className="text-[11px] font-medium text-gray-700 leading-tight" data-id="element-226">
                {t.hero.trust.text}
              </span>
            </div>
            <div className="flex items-center gap-1.5 border-t border-gray-200 pt-1.5" data-id="element-227">
              <ShieldCheck size={12} className="text-[#A855F7] shrink-0" data-id="element-228" />
              <span className="text-[10px] font-medium text-gray-600 leading-tight" data-id="element-229">
                {t.hero.trust.tags}
              </span>
            </div>
          </div>
        </div>
      </section>
    </>;
}